/** @jsx jsx */

/**
 * Render an app-level message.
 */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import classnames from 'classnames';

import { Typography2 } from '@coursera/cds-core';

type Props = {
  heading?: React.ReactNode;
  subheading?: React.ReactNode;
  className?: string;
  children?: React.ReactNode;
  size?: 'small' | 'large';
};

const styles = {
  root: css`
    width: 100%;
    text-align: center;
    background-color: var(--cds-color-neutral-background-primary-weak);
    color: var(--cds-color-neutral-primary-weak);

    &.large {
      padding: 0 var(--cds-spacing-1400);
    }

    &.large .heading-container {
      padding: var(--cds-spacing-400);
    }

    &.large .heading {
      margin: 0;
    }

    &.large .subheading {
      padding-top: var(--cds-spacing-50);
    }

    &.large .body {
      padding-bottom: var(--cds-spacing-400);
    }
  `,
};

const AppMessage: React.FC<Props> = ({ heading, subheading, className, children, size = 'large' }: Props) => {
  const HeadingContainer = heading ? (
    <Typography2 variant="subtitleMedium" className="heading" component="h2">
      {heading}
    </Typography2>
  ) : null;

  const SubheadingContainer = subheading ? (
    <Typography2 component="h3" variant="subtitleMedium" className="subheading">
      {subheading}
    </Typography2>
  ) : null;

  const classes = classnames('rc-AppMessage', size, className);

  return (
    <div className={classes} css={styles.root}>
      {(HeadingContainer || SubheadingContainer) && (
        <div className="heading-container">
          {HeadingContainer}
          {SubheadingContainer}
        </div>
      )}
      {children && (
        <Typography2 component="p" variant="bodyPrimary">
          {children}
        </Typography2>
      )}
    </div>
  );
};

export default AppMessage;
